<template>
  <span
    class="goblin-icon"
    :style="{ height: dim }"
    role="img"
    :aria-label="label"
    :aria-hidden="label ? undefined : 'true'"
    v-html="svg"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
// Raw SVG markup — inlined (not <img>) so `fill="currentColor"` inherits the
// surrounding text color and the mark recolors with any --lg-* token.
import mark from '../assets/goblin-mark.svg?raw';
import badge from '../assets/goblin-badge.svg?raw';
import empty from '../assets/goblin-empty.svg?raw';
import error from '../assets/goblin-error.svg?raw';

type Variant = 'mark' | 'badge' | 'empty' | 'error';

const props = withDefaults(
  defineProps<{
    /** Which goblin to render. */
    variant?: Variant;
    /** Any CSS length (number => px). Defaults to 1em so it tracks font-size. */
    size?: string | number;
    /** Accessible label; omit for a decorative icon (aria-hidden). */
    label?: string;
  }>(),
  { variant: 'mark', size: '1em', label: '' },
);

const sources: Record<Variant, string> = { mark, badge, empty, error };
const svg = computed(() => sources[props.variant]);
// `size` sets the icon HEIGHT; width follows each SVG's intrinsic viewBox
// aspect ratio. The traced mark/empty/error are wider than tall (~1.31:1),
// the badge is square — height-driven sizing lets each fill without letterbox.
const dim = computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size));
</script>

<style scoped>
.goblin-icon {
  display: inline-flex;
  color: inherit;
  line-height: 0;
}
.goblin-icon :deep(svg) {
  height: 100%;
  width: auto;
  display: block;
}
</style>
